import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import IconButton from "@mui/material/IconButton";
import SweetAlert from "react-bootstrap-sweetalert";
import PersonalCredentials from "./personalCredentials";
import ShieldIcon from "@mui/icons-material/Shield";
import VisibilityIcon from "@mui/icons-material/Visibility";

function PersonalCredentialsBox({ client }) {
  const [credentialsDetailsOpen, setCredentialsDetailsOpen] = useState(false);
  const [credentialsMonitoredNumber, setCredentialsMonitoredNumber] = useState(0);
  const [displayNumber, setDisplayNumber] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValue = useRef(0);
  const animationRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    let totalNumber = 0;

    if (client) {
      if (client.phones && client.phones.length > 0) {
        totalNumber += client.phones.length;
      }
      if (client.addresses && client.addresses.length > 0) {
        totalNumber += client.addresses.length;
      }
      if (client.ibans && client.ibans.length > 0) {
        totalNumber += client.ibans.length;
      }
      if (client.userCreditCards && client.userCreditCards.length > 0) {
        totalNumber += client.userCreditCards.length;
      }
      if (client.identifiers && client.identifiers.length > 0) {
        totalNumber += client.identifiers.length;
      }
      if (client.birthDates && client.birthDates.length > 0) {
        totalNumber += client.birthDates.length;
      }
    }

    setCredentialsMonitoredNumber(totalNumber);
  }, [client]);

  useEffect(() => {
    if (credentialsMonitoredNumber === 0) {
      setDisplayNumber(0);
      hasAnimatedRef.current = false;
      return;
    }

    const shouldAnimate =
      !hasAnimatedRef.current || prevValue.current !== credentialsMonitoredNumber;

    if (!shouldAnimate) {
      setDisplayNumber(credentialsMonitoredNumber);
      return;
    }

    setIsAnimating(true);
    hasAnimatedRef.current = true;

    const startTime = Date.now();
    const startValue = prevValue.current;
    const endValue = credentialsMonitoredNumber;
    const duration = 2000;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart);

      setDisplayNumber(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        prevValue.current = endValue;
        setTimeout(() => {
          setIsAnimating(false);
        }, 100);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [credentialsMonitoredNumber]);

  const handleCloseCredentialsDetails = () => {
    setCredentialsDetailsOpen(false);
    setTimeout(() => {
      document.body.classList.remove("sweet-alert-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const handleOpenCredentialsDetails = () => {
    document.body.classList.add("sweet-alert-open");
    document.body.style.overflow = "hidden";
    setCredentialsDetailsOpen(true);
  };

  const renderModal = () => {
    if (!credentialsDetailsOpen) return null;

    return createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <SweetAlert
          style={{
            display: "block",
            position: "relative",
            textAlign: "left",
            margin: 0,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "hidden",
          }}
          title={t("user.personal_credentials")}
          onConfirm={handleCloseCredentialsDetails}
          onCancel={handleCloseCredentialsDetails}
          closeOnClickOutside
          confirmBtnText={t("ok")}
          confirmBtnCssClass="popupCloseButton"
          confirmBtnStyle={{ boxShadow: "none" }}
        >
          <PersonalCredentials client={client} />
        </SweetAlert>
      </div>,
      document.body
    );
  };

  return (
    <>
      {renderModal()}

      <div
        className={`user_box_container enhanced-credential-box personal-credentials-box ${credentialsMonitoredNumber === 0 ? "empty-state" : ""}`}
      >
        <div className="box-decoration-element"></div>
        <div className="box-icon-container">
          <ShieldIcon className="box-main-icon" />
        </div>
        <div className="user_box_title">{t("user.personal_credentials")}</div>
        <div className="user_box_subtitle">
          {t("user.currently_personal_credentials_monitored")}
        </div>

        {credentialsMonitoredNumber === 0 ? (
          <div className="empty-state-message">
            <div className="empty-state-icon">
              <ShieldIcon />
            </div>
            <div className="empty-state-text">{t("user.no_credentials_title")}</div>
            <div className="empty-state-subtext">{t("user.no_credentials_text")}</div>
          </div>
        ) : (
          <>
            <div
              className={`user_box_number_credentials_monitored ${isAnimating ? "animating" : ""}`}
            >
              {displayNumber}
              <div className="number-pulse-effect"></div>
            </div>
            <div className="box-action-container">
              <IconButton
                onClick={handleOpenCredentialsDetails}
                className="enhanced-details-button"
                size="small"
                disableRipple
              >
                <VisibilityIcon className="button-icon" />
                <span>{t("user.show_credentials_details")}</span>
              </IconButton>
            </div>
          </>
        )}
      </div>
    </>
  );
}

PersonalCredentialsBox.propTypes = {
  client: PropTypes.object.isRequired,
};

export default PersonalCredentialsBox;
